import MarkdownIt from "markdown-it";
import type { JSX, ReactNode } from "react";

const mdSafe = new MarkdownIt("commonmark", { html: false });

export type CopyToClipboardProps =
    | {
          text: string;
          markdown?: never;
          html?: never;
          onCopy?: (text: string, result: boolean) => void;
          children: ReactNode;
      }
    | {
          markdown: string;
          text?: never;
          html?: never;
          onCopy?: (text: string, result: boolean) => void;
          children: ReactNode;
      }
    | {
          html: string;
          text?: string;
          markdown?: never;
          onCopy?: (text: string, result: boolean) => void;
          children: ReactNode;
      };

function stripHtml(html: string): string {
    const el = document.createElement("div");
    el.innerHTML = html;
    return el.textContent || el.innerText || "";
}

/**
 * Copies to clipboard using the modern Clipboard API.
 *
 * Modes:
 * - { text }       -> copies plain text only
 * - { markdown }   -> copies both text/plain (original markdown) and text/html (rendered)
 * - { html, text?} -> copies both text/html; text/plain uses provided text or a stripped fallback
 */
function CopyToClipboard({ children, onCopy, ...props }: CopyToClipboardProps): JSX.Element {
    const handleClick = async () => {
        let plainText: string;
        let htmlPayload: string | null = null;

        if ("markdown" in props) {
            plainText = props.markdown;
            htmlPayload = mdSafe.render(props.markdown);
        } else if ("html" in props) {
            htmlPayload = props.html;
            plainText = props.text ?? stripHtml(props.html);
        } else {
            plainText = props.text;
        }

        try {
            if (htmlPayload != null) {
                const item = new ClipboardItem({
                    "text/html": new Blob([htmlPayload], { type: "text/html" }),
                    "text/plain": new Blob([plainText], { type: "text/plain" }),
                });
                await navigator.clipboard.write([item]);
            } else {
                await navigator.clipboard.writeText(plainText);
            }
            onCopy?.(plainText, true);
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            try {
                await navigator.clipboard.writeText(plainText);
                onCopy?.(plainText, true);
            } catch {
                onCopy?.(plainText, false);
            }
        }
    };

    return (
        <span
            onClick={handleClick}
            onKeyDown={(e) => e.key === "Enter" && handleClick()}
            style={{ display: "contents" }}
        >
            {children}
        </span>
    );
}

export default CopyToClipboard;
